// @djsem/shared — Unified AI Provider Registry (Canonical Prompt 66).
//
// Global authority for AI providers across all engines (artwork, video, stem separation,
// prompt building, review). Centralizes capabilities, cost models, safety flags, plan
// gates, and fallback routing so AI modules never hardcode a provider.
//
// HARD RULES (from prompt 66 §7):
//   • Capabilities, costs, safety, plan gates and fallback routing are centralized here.
//   • No provider secrets in the frontend — this registry stores metadata only, never keys.
//   • Generated outputs must record provider/model metadata (see resolve()).

import { type DJSEMValidationResult, toDJSEMValidationResult, type ValidationResult } from "./index";
import type { DJSEMPlanLevel } from "./plan";
import type { DJSEMModelExecutionLocation } from "./modelExecutionAudit";
import type { DJSEMComputeTier } from "./computeTier";

/** The kind of AI-powered engine a model/provider serves. */
export type DJSEMAIEngineKind =
  | "artwork" | "video" | "stem_separation" | "prompt_build" | "generation_review" | "transcription"
  // Prompt 97 — AI Cue Director. Cue suggestion / lighting cue generation / semantic event
  // classification all register under this engine kind. Suggest-only; never drives hardware.
  | "cue_suggestion"
  // Prompt 148 (DJSEM AI Model Decision Report) — three engine kinds this repo had zero
  // integration for before this pass. Same enum values as DJSEMModelProviderCategory
  // (modelExecutionAudit.ts) by design: a resolve() from this registry is the thing a real
  // execution gets audited against.
  | "music_generation" | "voice_generation" | "symbolic_music";

/** Availability state of a provider or model within the registry. */
export type DJSEMAIProviderStatus = "enabled" | "disabled" | "beta" | "deprecated";

/** The registry's own simplified 5-tier plan vocabulary (see {@link planLevelToProviderPlanTier}). */
export type DJSEMPlanTier = "free" | "creator" | "creator_pro" | "performer" | "enterprise";

const PLAN_RANK: Record<DJSEMPlanTier, number> = {
  free: 0, creator: 1, creator_pro: 2, performer: 3, enterprise: 4,
};

/**
 * Collapse the canonical, billing-authoritative `DJSEMPlanLevel` (prompt 03A/03B, 7 tiers)
 * onto this registry's simpler 5-tier `DJSEMPlanTier`. These are two real, different plan
 * vocabularies in this repo (not a bug to silently unify) — `pro_integrations`/`venue` sit
 * between `performer` and `enterprise` in the billing model but have no AI-provider-registry
 * gate of their own yet, so they conservatively map to `"enterprise"` (the highest registry
 * tier) rather than being downgraded to `"performer"`: a user entitled to those plans should
 * never see LESS AI-model access than a performer-plan user.
 * @param level - The canonical, billing-authoritative plan level.
 * @returns The corresponding registry-scoped plan tier.
 */
export function planLevelToProviderPlanTier(level: DJSEMPlanLevel): DJSEMPlanTier {
  switch (level) {
    case "free_starter": return "free";
    case "creator": return "creator";
    case "creator_pro": return "creator_pro";
    case "performer": return "performer";
    case "pro_integrations": case "venue": case "enterprise": return "enterprise";
  }
}

/** A single AI model's advertised capabilities, cost, plan gate, and safety flags. */
export interface DJSEMAIModelCapability {
  modelId: string;
  label: string;
  engine: DJSEMAIEngineKind;
  status: DJSEMAIProviderStatus;
  /** Minimum plan tier allowed to use this model. */
  minPlan: DJSEMPlanTier;
  /** Credits per unit of work (per image / per second / per track etc.). */
  creditCostPerUnit: number;
  /** Whether this model can run in a non-hardware, safe/dry context. */
  safeForPublicOutput: boolean;
  /** Free-form capability tags (e.g. "1024x1024", "upscale", "4stem"). */
  capabilities: string[];
  warnings: string[];
  /**
   * Prompt 148: where this model actually runs if enabled — Cloudflare Workers AI, a local
   * CPU/GPU process, or an external GPU runner. Optional and undefined on entries registered
   * before this field existed; every new registration should set it so `resolve()`'s result
   * can be passed straight into DJSEMModelExecutionAuditService.startExecution().
   */
  executionLocation?: DJSEMModelExecutionLocation;
  /**
   * Prompt 149: local compute tiers this model can actually run on, best-first (e.g.
   * ["cuda_gpu","apple_silicon_gpu","cpu"] for a GPU-primary model that also works, slower, on
   * CPU; ["cpu","cuda_gpu","apple_silicon_gpu"] for a CPU-first fast/draft fallback family).
   * Only meaningful for `executionLocation: "local_cpu" | "local_gpu"` entries. Optional and
   * undefined on entries registered before this field existed or that don't run locally at all
   * (cloudflare_workers_ai, external_api, external_gpu_runner).
   */
  compatibleTiers?: DJSEMComputeTier[];
}

/** Registry-tracked metadata for an AI provider and the models it offers. Never contains secrets. */
export interface DJSEMAIProviderMetadata {
  providerId: string;
  label: string;
  status: DJSEMAIProviderStatus;
  /** Whether real credentials are configured server-side. NEVER the secret itself. */
  credentialConfigured: boolean;
  models: DJSEMAIModelCapability[];
}

/** Input to {@link DJSEMAIProviderRegistry.estimateCost}. */
export interface AICostEstimateInput {
  engine: DJSEMAIEngineKind;
  modelId: string;
  units: number;
}

/** Result of {@link DJSEMAIProviderRegistry.estimateCost}. */
export interface AICostEstimate {
  modelId: string;
  units: number;
  creditCost: number;
  warnings: string[];
}

/** Input to {@link DJSEMAIProviderRegistry.resolve}: what engine/plan/preference to resolve a model for. */
export interface AIResolveInput {
  engine: DJSEMAIEngineKind;
  plan: DJSEMPlanTier;
  preferredModelId?: string;
  requireSafePublicOutput?: boolean;
}

/** Result of {@link DJSEMAIProviderRegistry.resolve}. */
export interface AIResolveResult {
  ok: boolean;
  providerId?: string;
  model?: DJSEMAIModelCapability;
  /** Ordered fallback chain that was considered. */
  consideredModelIds: string[];
  blockers: string[];
  warnings: string[];
  /** Metadata to attach to any generated output (prompt 66 §7). */
  provenance?: { providerId: string; modelId: string };
}

/**
 * Global authority for AI providers across all DJSEM engines (artwork, video, stem
 * separation, prompt building, review, cue suggestion). Centralizes capabilities, cost
 * models, safety flags, plan gates, and fallback routing so AI modules never hardcode a
 * provider (canonical prompt 66).
 */
export class DJSEMAIProviderRegistry {
  private providers = new Map<string, DJSEMAIProviderMetadata>();

  /**
   * Registers (or replaces) a provider's metadata, keyed by `providerId`.
   * @param provider - The provider metadata to register.
   */
  register(provider: DJSEMAIProviderMetadata): void {
    this.providers.set(provider.providerId, provider);
  }

  /** @returns All registered providers. */
  listProviders(): DJSEMAIProviderMetadata[] {
    return [...this.providers.values()];
  }

  /**
   * Every model (any status) registered for an engine, across all providers, cheapest-first
   * (deterministic fallback order).
   * @param engine - The engine kind to list models for.
   * @returns The matching models, each tagged with its owning `providerId`.
   */
  capabilityMatrix(engine: DJSEMAIEngineKind): Array<DJSEMAIModelCapability & { providerId: string }> {
    const out: Array<DJSEMAIModelCapability & { providerId: string }> = [];
    for (const p of this.providers.values()) {
      for (const m of p.models) {
        if (m.engine === engine) out.push({ ...m, providerId: p.providerId });
      }
    }
    return out.sort((a, b) => a.creditCostPerUnit - b.creditCostPerUnit || a.modelId.localeCompare(b.modelId));
  }

  /**
   * Estimates the credit cost of running a given number of units on a specific model.
   * @param input - The engine, model id, and unit count to estimate for.
   * @returns The estimated cost, or a zero-cost result with a warning if the model is unknown.
   */
  estimateCost(input: AICostEstimateInput): AICostEstimate {
    const models = this.capabilityMatrix(input.engine);
    const model = models.find((m) => m.modelId === input.modelId);
    if (!model) return { modelId: input.modelId, units: input.units, creditCost: 0, warnings: ["Unknown model — cannot estimate cost."] };
    const units = Math.max(0, input.units);
    return { modelId: input.modelId, units, creditCost: Math.ceil(units * model.creditCostPerUnit), warnings: [] };
  }

  /**
   * Validates provider metadata before it's registered, rejecting anything missing a
   * `providerId` or containing a secret-looking field key.
   * @param provider - The provider metadata to validate.
   * @returns The validation result.
   */
  validateProviderMetadata(provider: DJSEMAIProviderMetadata): DJSEMValidationResult {
    const issues: ValidationResult["issues"] = [];
    if (!provider.providerId) issues.push({ path: "$.providerId", message: "required" });
    // Guard: never allow a secret-looking field to be stored.
    for (const k of Object.keys(provider as unknown as Record<string, unknown>)) {
      if (/apikey|api_key|secret|token|password/i.test(k)) issues.push({ path: `$.${k}`, message: "provider metadata must not contain secrets" });
    }
    return toDJSEMValidationResult({ valid: issues.length === 0, issues }, "ai_provider_metadata");
  }

  /**
   * Resolve the best model for an engine given the user's plan, honoring a preferred model
   * then falling back cheapest-first. Fails closed: if nothing is eligible, ok=false.
   * @param input - The engine, plan tier, optional preferred model id, and safety requirement.
   * @returns The resolved model/provider (with provenance) on success, or blockers/warnings
   *   explaining why nothing was eligible.
   */
  resolve(input: AIResolveInput): AIResolveResult {
    const all = this.capabilityMatrix(input.engine);
    const considered: string[] = [];
    const blockers: string[] = [];
    const warnings: string[] = [];

    const eligible = (m: DJSEMAIModelCapability & { providerId: string }): boolean => {
      considered.push(m.modelId);
      if (m.status === "disabled" || m.status === "deprecated") return false;
      if (PLAN_RANK[input.plan] < PLAN_RANK[m.minPlan]) return false;
      if (input.requireSafePublicOutput && !m.safeForPublicOutput) return false;
      const provider = this.providers.get(m.providerId);
      if (!provider || !provider.credentialConfigured) return false;
      return true;
    };

    // 1) Preferred model if it's eligible.
    if (input.preferredModelId) {
      const pref = all.find((m) => m.modelId === input.preferredModelId);
      if (pref && eligible(pref)) {
        return { ok: true, providerId: pref.providerId, model: pref, consideredModelIds: considered, blockers, warnings, provenance: { providerId: pref.providerId, modelId: pref.modelId } };
      }
      if (pref) warnings.push(`Preferred model ${input.preferredModelId} not eligible — falling back.`);
      else warnings.push(`Preferred model ${input.preferredModelId} not found — falling back.`);
    }

    // 2) Cheapest eligible fallback.
    for (const m of all) {
      if (m.modelId === input.preferredModelId) continue; // already tried
      if (eligible(m)) {
        return { ok: true, providerId: m.providerId, model: m, consideredModelIds: considered, blockers, warnings, provenance: { providerId: m.providerId, modelId: m.modelId } };
      }
    }

    blockers.push(`No eligible AI model for engine "${input.engine}" on plan "${input.plan}".`);
    return { ok: false, consideredModelIds: considered, blockers, warnings };
  }
}
